import * as Yup from 'yup';
import { TranslateFn } from '@blocs.i18n';
import { AddressValidationField, AddressValidationInput } from '@minecraft.graphql-operations';
import { shouldShowBillingStateField } from './shouldShowBillingStateField';
import {
  CA_POSTAL_CODE_MAX_LENGTH,
  CA_POSTAL_CODE_PATTERN,
  US_POSTAL_CODE_MAX_LENGTH,
  US_POSTAL_CODE_PATTERN,
} from './formatBillingPostalCode';

// https://docs.recurly.com/recurly-subscriptions/docs/tax#address-field-character-limits
// State/province and country are ISO alpha-2 codes validated by Recurly itself, so no client-side
// character limit is enforced for them here.
export const ADDRESS_FIELD_CHARACTER_LIMITS = {
  addressLine1: 50,
  addressLine2: 100,
  city: 50,
  postalCode: 11,
};

export const textField = (
  t: TranslateFn,
  { required, maxCharacters }: { required: boolean; maxCharacters?: number }
) => {
  const schema = maxCharacters
    ? Yup.string().trim().max(maxCharacters, t('common:validation.numberOfCharactersMax', { maxCharacters }))
    : Yup.string().trim();

  return required ? schema.required(t('common:validation.required')) : schema;
};

// Shared by both schemas below and defined outside addressFieldsSchema — postal_code is also rendered
// by the legacy (non-billing-address) zip field, which has no other address fields to gate on.
const postalCodeField = (t: TranslateFn, required: boolean) =>
  textField(t, { required, maxCharacters: ADDRESS_FIELD_CHARACTER_LIMITS.postalCode })
    .when('country', {
      is: (country?: string) => country?.toUpperCase() === 'US',
      then: textField(t, { required, maxCharacters: US_POSTAL_CODE_MAX_LENGTH }).matches(US_POSTAL_CODE_PATTERN, {
        // eslint-disable-next-line sonarjs/no-duplicate-string
        message: t('common:validation.invalidZipPostalCode'),
        excludeEmptyString: true,
      }),
    })
    .when('country', {
      is: (country?: string) => country?.toUpperCase() === 'CA',
      then: textField(t, { required, maxCharacters: CA_POSTAL_CODE_MAX_LENGTH }).matches(CA_POSTAL_CODE_PATTERN, {
        message: t('common:validation.invalidZipPostalCode'),
        excludeEmptyString: true,
      }),
    });

// address_line_2 is always optional. postal_code is defined separately by each caller (postalCodeField
// above), since it's also shared with the legacy (non-billing-address) zip field.
const addressFieldsSchema = (t: TranslateFn, required: boolean) => ({
  /* eslint-disable camelcase */
  country: textField(t, { required }),
  address_line_1: textField(t, { required, maxCharacters: ADDRESS_FIELD_CHARACTER_LIMITS.addressLine1 }),
  address_line_2: textField(t, { required: false, maxCharacters: ADDRESS_FIELD_CHARACTER_LIMITS.addressLine2 }),
  city: textField(t, { required, maxCharacters: ADDRESS_FIELD_CHARACTER_LIMITS.city }),
  // Recurly only requires a state/province for a handful of countries — required elsewhere would block
  // submission on a field that isn't even rendered (see BillingAddressFormLayout).
  state_region: Yup.string().when('country', {
    is: (country?: string) => shouldShowBillingStateField(country),
    then: textField(t, { required }),
    otherwise: Yup.string().trim(),
  }),
  /* eslint-enable camelcase */
});

/**
 * Single source of truth for what a payment method form accepts. Submission is never blocked by a
 * disabled button — Formik runs this on submit and surfaces any errors inline instead, leaving the
 * card fields themselves to be validated by Recurly at tokenization time.
 *
 * @param areCardFieldsVisible whether the card/billing fields are currently rendered. Fields that are
 * not on screen must not be required, or submission would be blocked by errors the user cannot see.
 */
export const createPaymentMethodValidationSchema = (
  t: TranslateFn,
  isPaywallBillingAddressEnabled?: boolean,
  areCardFieldsVisible = true
) => {
  const required = areCardFieldsVisible;

  return Yup.object().shape({
    // Field names mirror Recurly's `data-recurly` tokens, hence the snake_case.
    /* eslint-disable camelcase */
    first_name: textField(t, { required }),
    last_name: textField(t, { required }),
    // postal_code is rendered by both layouts — the legacy zip field when the billing address flag is
    // off and the billing address section when it's on — so its limit applies either way.
    postal_code: postalCodeField(t, required),
    /* eslint-enable camelcase */
    ...(isPaywallBillingAddressEnabled && addressFieldsSchema(t, required)),
  });
};

/**
 * Validation for the wallet picker's billing-address-only editor (WalletPicker.tsx), which has no
 * card/name fields to gate required-ness on — the address fields are always the entire form, so
 * they're always required (address_line_2 excepted, as above).
 */
export const createBillingAddressValidationSchema = (t: TranslateFn) =>
  Yup.object().shape({
    ...addressFieldsSchema(t, true),
    // eslint-disable-next-line camelcase
    postal_code: postalCodeField(t, true),
  });

/**
 * Same required/format rules as createBillingAddressValidationSchema above (shared constants and
 * textField, not re-derived), keyed to AddressValidationForm's own namespaced Formik field names
 * instead of BillingAddressFields' — that modal can't reuse those names since its form stays mounted
 * on top of the main billing-address form it's validating a correction for.
 *
 * Also carries the modal's own "still matches what Google rejected" rule as a `.test()` on each
 * flagged field
 */
export const createAddressValidationModalSchema = (
  t: TranslateFn,
  flaggedFields: AddressValidationField[],
  lastCheckedInput: AddressValidationInput
) =>
  Yup.object().shape({
    validationCountry: textField(t, { required: true }).test(
      'still-matches-google-rejected-country',
      t('common:validation.invalidCountry'),
      (value) => !(flaggedFields.includes(AddressValidationField.Country) && value === lastCheckedInput.regionCode)
    ),
    validationLine1: textField(t, { required: true, maxCharacters: ADDRESS_FIELD_CHARACTER_LIMITS.addressLine1 }),
    validationLine2: textField(t, { required: false, maxCharacters: ADDRESS_FIELD_CHARACTER_LIMITS.addressLine2 }),
    validationCity: textField(t, { required: true, maxCharacters: ADDRESS_FIELD_CHARACTER_LIMITS.city }).test(
      'still-matches-google-rejected-city',
      t('common:validation.invalidCity'),
      (value) => !(flaggedFields.includes(AddressValidationField.City) && value === lastCheckedInput.locality)
    ),
    validationState: Yup.string()
      .when('validationCountry', {
        is: (country?: string) => shouldShowBillingStateField(country),
        then: textField(t, { required: true }),
        otherwise: Yup.string().trim(),
      })
      .test(
        'still-matches-google-rejected-state',
        t('common:validation.invalidStateProvince'),
        (value) =>
          !(flaggedFields.includes(AddressValidationField.State) && value === lastCheckedInput.administrativeArea)
      ),
    validationPostalCode: textField(t, { required: true, maxCharacters: ADDRESS_FIELD_CHARACTER_LIMITS.postalCode })
      .when('validationCountry', {
        is: (country?: string) => country?.toUpperCase() === 'US',
        then: textField(t, { required: true, maxCharacters: US_POSTAL_CODE_MAX_LENGTH }).matches(
          US_POSTAL_CODE_PATTERN,
          { message: t('common:validation.invalidZipPostalCode'), excludeEmptyString: true }
        ),
      })
      .when('validationCountry', {
        is: (country?: string) => country?.toUpperCase() === 'CA',
        then: textField(t, { required: true, maxCharacters: CA_POSTAL_CODE_MAX_LENGTH }).matches(
          CA_POSTAL_CODE_PATTERN,
          { message: t('common:validation.invalidZipPostalCode'), excludeEmptyString: true }
        ),
      })
      .test(
        'still-matches-google-rejected-postal-code',
        t('common:validation.invalidZipPostalCode'),
        (value) => !(flaggedFields.includes(AddressValidationField.PostalCode) && value === lastCheckedInput.postalCode)
      ),
  });
